"use client";

import axios from "axios";
import { useCallback, useState } from "react";

import { errorMessage } from "@/i18n";
import { convertToHiragana } from "@/util/hiragana";
import { showNotification } from "@/util/userNotifications";
import {
  convertHalfWidthDigitsToKanji,
  directFetchBranchCodes,
  isHalfWidthNumericBranchInput,
  YUCHO_BANK_CODE,
} from "@/util/yuchoBranchSearch";

export type Banks = {
  data: Bank[];
  size: number;
  limit: number;
};

export type Bank = {
  code: string;
  name: string;
  halfWidthKana: string;
  fullWidthKana: string;
  hiragana: string;
  businessTypeCode: string;
  businessType: string;
};

export type BranchItem = {
  code: string;
  name: string;
  halfWidthKana: string;
  fullWidthKana: string;
  hiragana: string;
};

type Branch = BranchItem[];

type ErrorResponse = {
  httpStatusCode: number;
  code: string;
  message: string;
  validationErrors: {
    message: string;
    paramName: string;
    invalidValue: string;
  }[];
};

const BANKCODE_API_BASE = "https://apis.bankcode-jp.com/v2";

function isBankCodeApiRateLimited(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 429;
}

function isRateLimitedErrorResponse(data: ErrorResponse): boolean {
  return data.httpStatusCode === 429;
}

function notifyBankCodeLookupFailure(
  notFoundKey: "notFoundBankCode" | "notFoundBranchCode",
  error?: unknown,
  data?: ErrorResponse,
) {
  if (error !== undefined && isBankCodeApiRateLimited(error)) {
    return;
  }
  if (data !== undefined && isRateLimitedErrorResponse(data)) {
    return;
  }
  showNotification({
    type: "caution",
    message: errorMessage(notFoundKey),
  });
}

const bankCodeApiHeaders = () => ({
  apikey: process.env.NEXT_PUBLIC_BANKCODE_API_KEY,
});

export function isErrorResponse(
  data: Banks | Bank | Branch | BranchItem | ErrorResponse,
): data is ErrorResponse {
  return "httpStatusCode" in data;
}

function normalizeBranchList(data: unknown): BranchItem[] {
  if (Array.isArray(data)) {
    return data as BranchItem[];
  }
  if (data && typeof data === "object" && "data" in data) {
    const nested = (data as Banks).data;
    return Array.isArray(nested) ? (nested as BranchItem[]) : [];
  }
  if (data && typeof data === "object" && "code" in data && "name" in data) {
    return [data as BranchItem];
  }
  return [];
}

function mergeBranchesByCode(lists: BranchItem[][]): BranchItem[] {
  const seen = new Set<string>();
  const merged: BranchItem[] = [];

  for (const list of lists) {
    for (const branch of list) {
      if (seen.has(branch.code)) continue;
      seen.add(branch.code);
      merged.push(branch);
    }
  }

  return merged;
}

function buildBranchNameFilter(input: string, hiraganaInput: string): string {
  return `filter=(name==${input}*,hiragana==${hiraganaInput}*)&`;
}

function buildBranchCodeFilter(codePrefix: string): string {
  return `filter=(code==${codePrefix}*)&`;
}

async function fetchBranchList(
  bankCode: string,
  filterParam: string,
  limitParam: string,
): Promise<BranchItem[] | undefined> {
  try {
    const res = await axios.get<Banks | ErrorResponse>(
      `${BANKCODE_API_BASE}/banks/${bankCode}/branches?${limitParam}&fields=code,name&sort=hiragana&${filterParam}`,
      { headers: bankCodeApiHeaders() },
    );

    const { data } = res;
    if (isErrorResponse(data)) {
      return undefined;
    }

    return normalizeBranchList(data);
  } catch {
    return undefined;
  }
}

async function fetchBranchByCode(
  bankCode: string,
  branchCode: string,
): Promise<BranchItem | undefined> {
  try {
    const res = await axios.get<BranchItem | Branch | ErrorResponse>(
      `${BANKCODE_API_BASE}/banks/${bankCode}/branches/${branchCode}`,
      { headers: bankCodeApiHeaders() },
    );

    const { data } = res;
    if (isErrorResponse(data)) {
      return undefined;
    }

    const branches = normalizeBranchList(data);
    return branches[0];
  } catch {
    return undefined;
  }
}

async function searchYuchoBranchesByNumericInput(
  bankCode: string,
  input: string,
): Promise<BranchItem[] | undefined> {
  const trimmed = input.trim();
  const kanjiInput = convertHalfWidthDigitsToKanji(trimmed);
  const kanjiHiraganaInput = await convertToHiragana(kanjiInput);

  const [directBranches, listSearchResults] = await Promise.all([
    Promise.all(
      directFetchBranchCodes(trimmed).map((branchCode) =>
        fetchBranchByCode(bankCode, branchCode),
      ),
    ),
    Promise.allSettled([
      fetchBranchList(bankCode, buildBranchCodeFilter(trimmed), "limit=200"),
      fetchBranchList(
        bankCode,
        buildBranchNameFilter(kanjiInput, kanjiHiraganaInput),
        "limit=200",
      ),
    ]),
  ]);

  const listSearches = listSearchResults.map((result) =>
    result.status === "fulfilled" ? result.value : undefined,
  );

  const merged = mergeBranchesByCode([
    directBranches.flatMap((branch) => (branch ? [branch] : [])),
    ...listSearches.map((result) => result ?? []),
  ]);

  if (merged.length > 0) {
    return merged;
  }

  const hadNetworkFailure = listSearches.some((result) => result === undefined);
  return hadNetworkFailure ? undefined : [];
}

export function useGetBankResourcesByCode() {
  const [loading, setLoading] = useState(false);
  const getBank = async ({
    bankcode,
  }: {
    bankcode: string;
  }): Promise<Bank | void> => {
    if (bankcode === "") return;
    setLoading(true);
    const res = await axios
      .get<Bank | ErrorResponse>(`${BANKCODE_API_BASE}/banks/${bankcode}`, {
        headers: bankCodeApiHeaders(),
      })
      .then((res) => {
        const { data } = res;
        if (isErrorResponse(data)) {
          notifyBankCodeLookupFailure("notFoundBankCode", undefined, data);
          return;
        }
        return data;
      })
      .catch((error) => {
        notifyBankCodeLookupFailure("notFoundBankCode", error);
      });
    setLoading(false);
    return res;
  };

  return { getBank, loading };
}

export function useGetBankBrancheResourcesByCode() {
  const [loading, setLoading] = useState(false);
  const getBankBranche = async ({
    bankcode,
    branchcode,
  }: {
    bankcode: string;
    branchcode: string;
  }): Promise<Branch | void> => {
    if (bankcode === "") return;
    if (branchcode === "") return;
    setLoading(true);
    const res = await axios
      .get<Branch | ErrorResponse>(
        `${BANKCODE_API_BASE}/banks/${bankcode}/branches/${branchcode}`,
        {
          headers: bankCodeApiHeaders(),
        },
      )
      .then((res) => {
        const { data } = res;
        if (isErrorResponse(data)) {
          notifyBankCodeLookupFailure("notFoundBranchCode", undefined, data);
          return;
        }

        return data;
      })
      .catch((error) => {
        notifyBankCodeLookupFailure("notFoundBranchCode", error);
      });
    setLoading(false);
    return res;
  };
  return { getBankBranche, loading };
}

export const useGetBanks = () => {
  const [loading, setLoading] = useState(false);
  const getFilterBank = useCallback(
    async (input: string): Promise<Bank[] | ErrorResponse | undefined> => {
      if (input === "") return undefined;

      setLoading(true);
      const hiraganaInput = await convertToHiragana(input);
      const filter = `filter=(name==${input}*,hiragana==${hiraganaInput}*)`;
      const res = await axios
        .get<Banks | ErrorResponse>(
          `${BANKCODE_API_BASE}/banks/?limit=20&fields=code,name&sort=hiragana&${filter}`,
          {
            headers: bankCodeApiHeaders(),
          },
        )
        .then((res) => {
          const { data } = res;
          if (isErrorResponse(data)) {
            notifyBankCodeLookupFailure("notFoundBankCode", undefined, data);
            return undefined;
          }

          return data;
        })
        .catch((error) => {
          notifyBankCodeLookupFailure("notFoundBankCode", error);
          return undefined;
        });
      setLoading(false);
      return res?.data;
    },
    [],
  );

  return { getFilterBank, loading };
};

export const useGetBankBranches = () => {
  const [loading, setLoading] = useState(false);
  const getBranches = useCallback(
    async (
      bankCode: string,
      input: string,
    ): Promise<Branch | ErrorResponse | undefined> => {
      setLoading(true);

      try {
        const trimmedInput = input.trim();
        const isYuchoNumericSearch =
          bankCode === YUCHO_BANK_CODE &&
          trimmedInput !== "" &&
          isHalfWidthNumericBranchInput(trimmedInput);

        if (isYuchoNumericSearch) {
          const yuchoResult = await searchYuchoBranchesByNumericInput(
            bankCode,
            trimmedInput,
          );

          if (yuchoResult === undefined) {
            return undefined;
          }

          return yuchoResult;
        }

        let filterParam = "";
        let limitParam = "limit=100";

        if (trimmedInput !== "") {
          const hiraganaInput = await convertToHiragana(trimmedInput);
          filterParam = buildBranchNameFilter(trimmedInput, hiraganaInput);
          limitParam = "limit=200";
        }

        const res = await axios
          .get<Banks | ErrorResponse>(
            `${BANKCODE_API_BASE}/banks/${bankCode}/branches?${limitParam}&fields=code,name&sort=hiragana&${filterParam}`,
            { headers: bankCodeApiHeaders() },
          )
          .then((response) => {
            const { data } = response;
            if (isErrorResponse(data)) {
              return undefined;
            }

            return normalizeBranchList(data);
          })
          .catch(() => undefined);

        return res;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { getBranches, loading };
};
